import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

type Area = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const IMAGE_BASE_WIDTH = 2000;

type Props = {
  imageRef: MutableRefObject<HTMLImageElement>;
  areaRef: MutableRefObject<HTMLDivElement>;
};

export const useMoveableClipArea = ({ imageRef, areaRef }: Props) => {
  const [area, setArea] = useState<Area | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const resizeStartAreaRef = useRef<Area | null>(null);

  const dragStartAreaRef = useRef<Area | null>(null);
  const moveableRef = useRef<any>(null);

  useEffect(() => {
    // ドラッグやリサイズ中はupdateRectを呼ばない。
    if (moveableRef.current && !isDragging) {
      moveableRef.current.updateRect();
    }
  }, [area, isDragging]);

  const initialAreaRef = useRef<Area | undefined>(undefined);

  const mapRatio = useMemo(() => {
    return imageRef.current?.offsetWidth ? imageRef.current.offsetWidth / IMAGE_BASE_WIDTH : 1;
  }, [imageRef.current?.offsetWidth]);

  const initArea = useCallback(
    (initialArea?: Area) => {
      if (!imageRef.current) return;
      const { offsetWidth, offsetHeight } = imageRef.current;

      initialAreaRef.current = initialArea;

      if (initialArea) {
        setArea({
          left: initialArea.left * mapRatio,
          top: initialArea.top * mapRatio,
          width: initialArea.width * mapRatio,
          height: initialArea.height * mapRatio,
        });
        return;
      }

      const initialSize = 100 * mapRatio;
      const left = (offsetWidth - initialSize) / 2;
      const top = (offsetHeight - initialSize) / 2;
      setArea({
        left,
        top,
        width: initialSize,
        height: initialSize,
      });
    },
    [imageRef, mapRatio]
  );

  useEffect(() => {
    if (area && initialAreaRef.current) {
      // initialAreaがある場合、mapRatioの変化に合わせて再計算する
      setArea({
        left: initialAreaRef.current.left * mapRatio,
        top: initialAreaRef.current.top * mapRatio,
        width: initialAreaRef.current.width * mapRatio,
        height: initialAreaRef.current.height * mapRatio,
      });
    }
  }, [mapRatio]);

  const onDragStart = useCallback(() => {
    dragStartAreaRef.current = area;
  }, [area]);

  const onDrag = useCallback(
    ({ beforeTranslate }) => {
      if (!imageRef.current || !dragStartAreaRef.current || !areaRef.current) return;

      const { offsetWidth, offsetHeight } = imageRef.current;
      const startArea = dragStartAreaRef.current;
      const target = areaRef.current;

      let x = beforeTranslate[0];
      let y = beforeTranslate[1];

      if (startArea.left + x < 0) {
        x = -startArea.left;
      } else if (startArea.left + x + startArea.width > offsetWidth) {
        x = offsetWidth - startArea.left - startArea.width;
      }

      if (startArea.top + y < 0) {
        y = -startArea.top;
      } else if (startArea.top + y + startArea.height > offsetHeight) {
        y = offsetHeight - startArea.top - startArea.height;
      }

      target.style.transform = `translate(${x}px, ${y}px)`;
      setIsDragging(true);
    },
    [imageRef, areaRef]
  );

  const onDragEnd = useCallback(
    ({ isDrag }) => {
      if (!isDrag || !areaRef.current) {
        setIsDragging(false);
        dragStartAreaRef.current = null;
        return;
      }

      const target = areaRef.current;
      const style = window.getComputedStyle(target);
      const matrix = new DOMMatrixReadOnly(style.transform);
      const translateX = matrix.m41;
      const translateY = matrix.m42;

      setArea((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          left: prev.left + translateX,
          top: prev.top + translateY,
        };
      });

      // Reactの再描画後にtransformをクリアすることで一瞬戻るのを防ぐ
      requestAnimationFrame(() => {
        target.style.transform = "";
        // transformクリア後にMoveableの位置を再同期する
        if (moveableRef.current) {
          moveableRef.current.updateRect();
        }
      });
      setIsDragging(false);
      dragStartAreaRef.current = null;
    },
    [areaRef]
  );

  const onResizeStart = useCallback(() => {
    resizeStartAreaRef.current = area;
  }, [area]);

  const onResize = useCallback(
    ({ width, height, direction, drag }) => {
      if (!imageRef.current || !resizeStartAreaRef.current || !areaRef.current) return;
      const { offsetWidth, offsetHeight } = imageRef.current;
      const startArea = resizeStartAreaRef.current;
      const target = areaRef.current;

      let w = width;
      let h = height;
      let x = drag.beforeTranslate[0];
      let y = drag.beforeTranslate[1];

      // 最小サイズ制限
      const minSize = 20;

      if (direction[0] === -1) {
        // 左辺を操作中
        if (startArea.left + x < 0) {
          x = -startArea.left;
          w = startArea.width + startArea.left;
        } else if (w < minSize) {
          w = minSize;
          x = startArea.width - minSize;
        }
      } else if (direction[0] === 1) {
        // 右辺を操作中
        if (startArea.left + w > offsetWidth) {
          w = offsetWidth - startArea.left;
        } else if (w < minSize) {
          w = minSize;
        }
      }

      if (direction[1] === -1) {
        // 上辺を操作中
        if (startArea.top + y < 0) {
          y = -startArea.top;
          h = startArea.height + startArea.top;
        } else if (h < minSize) {
          h = minSize;
          y = startArea.height - minSize;
        }
      } else if (direction[1] === 1) {
        // 下辺を操作中
        if (startArea.top + h > offsetHeight) {
          h = offsetHeight - startArea.top;
        } else if (h < minSize) {
          h = minSize;
        }
      }

      target.style.width = `${Math.round(w)}px`;
      target.style.height = `${Math.round(h)}px`;
      target.style.transform = `translate(${x}px, ${y}px)`;

      setIsDragging(true);
    },
    [imageRef, areaRef]
  );

  const onResizeEnd = useCallback(
    ({ isDrag }) => {
      if (!isDrag || !areaRef.current) {
        setIsDragging(false);
        resizeStartAreaRef.current = null;
        return;
      }

      const target = areaRef.current;
      const style = window.getComputedStyle(target);
      const matrix = new DOMMatrixReadOnly(style.transform);
      const translateX = matrix.m41;
      const translateY = matrix.m42;
      const width = parseInt(target.style.width);
      const height = parseInt(target.style.height);

      setArea((prev) => {
        if (!prev) return null;
        return {
          left: prev.left + translateX,
          top: prev.top + translateY,
          width,
          height,
        };
      });

      // Reactの再描画後にtransformをクリアすることで一瞬戻るのを防ぐ
      requestAnimationFrame(() => {
        target.style.transform = "";
        // transformクリア後にMoveableの位置を再同期する
        if (moveableRef.current) {
          moveableRef.current.updateRect();
        }
      });
      setIsDragging(false);
      resizeStartAreaRef.current = null;
    },
    [areaRef]
  );

  return {
    area,
    initArea,
    onDrag,
    onDragEnd,
    onDragStart,
    onResizeStart,
    onResize,
    onResizeEnd,
    isDragging,
    mapRatio,
    moveableRef,
  };
};
